// src/api/zonasMesa.js
import { apiFetch } from './index.js';
import { toTitleCase } from '@/utils/stringUtils.js';

const convertirDesdeAPI = (apiZona) => {
  if (!apiZona) return null;
  let source = apiZona;
  if (apiZona.data && !Array.isArray(apiZona.data) && typeof apiZona.data === 'object') source = apiZona.data;
  else if (apiZona.zona && typeof apiZona.zona === 'object') source = apiZona.zona;

  const getProp = (obj, propName) => {
    if (!obj) return undefined;
    const keys = Object.keys(obj);
    const normalizedProp = propName.toLowerCase().replace(/_/g, '');
    const foundKey = keys.find(k => k.toLowerCase().replace(/_/g, '') === normalizedProp);
    return foundKey ? obj[foundKey] : undefined;
  };

  const id = source.idZonaMesa || source.idzonaMesa || source.id_zona_mesa ||
             source.IdZonaMesa || source.id ||
             getProp(source, 'idZonaMesa') || getProp(source, 'id');

  return {
    idZonaMesa: id, id: id,
    nombre: toTitleCase(source.nombre || source.Nombre || getProp(source, 'nombre') || ''),
    descripcion: source.descripcion || source.Descripcion || getProp(source, 'descripcion') || '',
    estado: source.estado || source.Estado || getProp(source, 'estado') || 'activo',
    fecha_creacion: source.fecha_creacion || source.fechaCreacion || source.FechaCreacion || getProp(source, 'fecha_creacion'),
    totalMesas: source.totalMesas ?? source.TotalMesas ?? 0,
    mesasLibres: source.mesasLibres ?? source.MesasLibres ?? 0,
    mesasOcupadas: source.mesasOcupadas ?? source.MesasOcupadas ?? 0,
    ...apiZona, ...source, idZonaMesa: id,
  };
};

const convertirParaAPI = (frontendZona) => ({
  nombre: toTitleCase(frontendZona.nombre || ''),
  descripcion: frontendZona.descripcion || '',
  estado: frontendZona.estado || 'activo',
});

const convertirListaDesdeAPI = (apiZonas) => {
  let lista = apiZonas;
  if (apiZonas && apiZonas.data && Array.isArray(apiZonas.data)) lista = apiZonas.data;
  else if (apiZonas && apiZonas.zonas && Array.isArray(apiZonas.zonas)) lista = apiZonas.zonas;
  if (!Array.isArray(lista)) return [];
  return lista.map(convertirDesdeAPI);
};

export const formatearFechaZona = (fechaISO, completo = false) => {
  if (!fechaISO) return 'N/A';
  try {
    const fecha = new Date(fechaISO);
    const opciones = { year: 'numeric', month: 'short', day: 'numeric' };
    if (completo) { opciones.hour = '2-digit'; opciones.minute = '2-digit'; }
    return fecha.toLocaleDateString('es-ES', opciones);
  } catch { return fechaISO; }
};

export const convertirEstadoATexto = (estado) => {
  if (!estado) return 'Sin estado';
  return estado.charAt(0).toUpperCase() + estado.slice(1);
};

export const convertirEstadoABoolean = (estado) => estado?.toLowerCase() === 'activo';

export const obtenerEstadisticasZonas = (zonas) => {
  const activas = zonas.filter(z => z.estado?.toLowerCase() === 'activo').length;
  const inactivas = zonas.filter(z => z.estado?.toLowerCase() === 'inactivo').length;
  const totalMesas = zonas.reduce((acc, z) => acc + (z.totalMesas || 0), 0);
  const mesasLibres = zonas.reduce((acc, z) => acc + (z.mesasLibres || 0), 0);
  const mesasOcupadas = zonas.reduce((acc, z) => acc + (z.mesasOcupadas || 0), 0);
  return {
    total: zonas.length, activas, inactivas,
    porcentajeActivas: zonas.length > 0 ? Math.round((activas / zonas.length) * 100) : 0,
    totalMesas, mesasLibres, mesasOcupadas,
    porcentajeOcupacion: totalMesas > 0 ? Math.round((mesasOcupadas / totalMesas) * 100) : 0,
  };
};

export const buscarZonas = (zonas, termino) => {
  if (!termino || termino.trim() === '') return zonas;
  const t = termino.toLowerCase();
  return zonas.filter(z =>
    z.nombre?.toLowerCase().includes(t) ||
    z.descripcion?.toLowerCase().includes(t) ||
    convertirEstadoATexto(z.estado)?.toLowerCase().includes(t)
  );
};

export const filtrarZonasPorEstado = (zonas, estado) => {
  if (estado === 'all') return zonas;
  if (estado === 'activos') return zonas.filter(z => z.estado?.toLowerCase() === 'activo');
  if (estado === 'inactivos') return zonas.filter(z => z.estado?.toLowerCase() === 'inactivo');
  return zonas;
};

export const obtenerZonas = async () => {
  try {
    console.log('📋 Obteniendo zonas de mesa...');
    const response = await apiFetch('/ZonaMesa');
    const zonas = convertirListaDesdeAPI(response);
    return { success: true, data: zonas, count: zonas.length, message: 'Zonas obtenidas exitosamente' };
  } catch (error) {
    console.error('❌ Error obteniendo zonas:', error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

export const obtenerZonasActivas = async () => {
  try {
    const response = await apiFetch('/ZonaMesa/activas');
    const zonas = convertirListaDesdeAPI(response);
    return { success: true, data: zonas, count: zonas.length, message: 'Zonas activas obtenidas' };
  } catch (error) {
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

export const obtenerZonasPorEstado = async (estado) => {
  try {
    if (!estado) throw new Error('Estado es requerido');
    const response = await apiFetch(`/ZonaMesa/estado/${estado}`);
    const zonas = convertirListaDesdeAPI(response);
    return { success: true, data: zonas, count: zonas.length, message: 'Zonas obtenidas' };
  } catch (error) {
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

export const obtenerZonaPorId = async (id) => {
  try {
    if (!id) throw new Error('ID de zona es requerido');
    const response = await apiFetch(`/ZonaMesa/${id}`);
    return { success: true, data: convertirDesdeAPI(response), message: 'Zona obtenida' };
  } catch (error) {
    return { success: false, error: error.message, data: null };
  }
};

export const obtenerZonaConRelaciones = async (id) => {
  try {
    if (!id) throw new Error('ID de zona es requerido');
    const response = await apiFetch(`/ZonaMesa/${id}/conRelaciones`);
    return { success: true, data: convertirDesdeAPI(response), message: 'Zona con relaciones' };
  } catch (error) {
    return { success: false, error: error.message, data: null };
  }
};

export const buscarZonasPorCriterios = async (criterios = {}) => {
  try {
    const response = await apiFetch('/ZonaMesa/search', {
      method: 'POST',
      body: JSON.stringify({ nombre: criterios.nombre || null, estado: criterios.estado || null }),
    });
    const zonas = convertirListaDesdeAPI(response);
    return { success: true, data: zonas, count: zonas.length, message: 'Búsqueda completada' };
  } catch (error) {
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

export const verificarZonaPorId = async (id) => {
  try {
    const response = await apiFetch(`/ZonaMesa/${id}/exists`);
    return { success: true, existe: response.exists || false, message: 'Verificado' };
  } catch (error) {
    return { success: false, error: error.message, existe: false };
  }
};

export const crearZona = async (zonaData) => {
  try {
    if (!zonaData.nombre || zonaData.nombre.trim() === '') throw new Error('El nombre es requerido');
    const apiData = convertirParaAPI(zonaData);
    const response = await apiFetch('/ZonaMesa', { method: 'POST', body: JSON.stringify(apiData) });
    return { success: true, data: convertirDesdeAPI(response), message: 'Zona creada exitosamente' };
  } catch (error) {
    return { success: false, error: error.message, data: null };
  }
};

export const actualizarZona = async (id, zonaData) => {
  try {
    if (!id) throw new Error('ID de zona es requerido');
    if (!zonaData.nombre || zonaData.nombre.trim() === '') throw new Error('El nombre es requerido');
    const apiData = convertirParaAPI(zonaData);
    const response = await apiFetch(`/ZonaMesa/${id}`, { method: 'PUT', body: JSON.stringify(apiData) });
    return { success: true, data: convertirDesdeAPI(response), message: 'Zona actualizada exitosamente' };
  } catch (error) {
    return { success: false, error: error.message, data: null };
  }
};

// 👇 FUNCIÓN CORREGIDA: Captura el error exacto del backend para mostrarlo
export const eliminarZona = async (id) => {
  try {
    if (!id) throw new Error('ID de zona es requerido');
    console.log(`🗑️ Eliminando permanentemente la zona ID: ${id}`);
    
    const response = await apiFetch(`/ZonaMesa/${id}`, { method: 'DELETE' });
    
    return {
      success: true,
      data: response,
      message: response?.mensaje || 'Zona eliminada permanentemente',
    };
  } catch (error) {
    console.error(`❌ Error eliminando zona ${id}:`, error);
    
    // Extraemos el mensaje del backend. apiFetch suele devolver el JSON de error en error.response.data
    let errorMsg = 'Error al eliminar la zona';
    
    if (error.response && error.response.data && error.response.data.mensaje) {
      errorMsg = error.response.data.mensaje;
    } else if (error.response && error.response.data && error.response.data.error) {
      errorMsg = error.response.data.error;
    } else if (error.message) {
      errorMsg = error.message;
    }

    return { success: false, error: errorMsg, data: null };
  }
};

export const desactivarZona = async (id) => {
  try {
    const zonaResult = await obtenerZonaPorId(id);
    if (!zonaResult.success || !zonaResult.data) throw new Error('No se pudo obtener la zona');
    const zona = zonaResult.data;
    zona.estado = 'inactivo';
    return await actualizarZona(id, zona);
  } catch (error) {
    return { success: false, error: error.message, data: null };
  }
};

export const activarZona = async (id) => {
  try {
    const zonaResult = await obtenerZonaPorId(id);
    if (!zonaResult.success || !zonaResult.data) throw new Error('No se pudo obtener la zona');
    const zona = zonaResult.data;
    zona.estado = 'activo';
    return await actualizarZona(id, zona);
  } catch (error) {
    return { success: false, error: error.message, data: null };
  }
};

export default {
  obtenerZonas, obtenerZonasActivas, obtenerZonasPorEstado, obtenerZonaPorId,
  obtenerZonaConRelaciones, buscarZonasPorCriterios, verificarZonaPorId,
  crearZona, actualizarZona, eliminarZona, activarZona, desactivarZona,
  formatearFechaZona, convertirEstadoATexto, convertirEstadoABoolean,
  obtenerEstadisticasZonas, buscarZonas, filtrarZonasPorEstado,
  convertirDesdeAPI, convertirParaAPI,
};